/**
 * OpenAPI conversion
 *
 * Converts OpenAPI schemas into OpenAI function definitions and route maps.
 *
 * Primary responsibilities:
 * - Validate minimal OpenAPI schema requirements
 * - Convert HTTP operations into callable tool functions
 * - Preserve method and path metadata for execution
 *
 * Dependencies:
 * - OpenAPI schema objects
 */

// import $RefParser from "@apidevtools/json-schema-ref-parser"

const OPENAPI_HTTP_METHODS = new Set([
  'get',
  'post',
  'put',
  'patch',
  'delete',
  'options',
  'head',
  'trace'
])

function isOpenApiHttpMethod(method: string) {
  return OPENAPI_HTTP_METHODS.has(method.toLowerCase())
}

function getOpenApiOperationEntries(methods: unknown) {
  if (!methods || typeof methods !== 'object' || Array.isArray(methods)) {
    return []
  }

  return Object.entries(methods as Record<string, unknown>).filter(([method, spec]) => {
    return isOpenApiHttpMethod(method) && spec && typeof spec === 'object' && !Array.isArray(spec)
  })
}

function getOpenApiParameters(value: unknown) {
  return Array.isArray(value) ? value : []
}

function mergeOpenApiParameters(pathParameters: unknown[], operationParameters: unknown[]) {
  const merged = new Map<string, unknown>()
  const allParameters = [...pathParameters, ...operationParameters]

  allParameters.forEach((parameter) => {
    if (!parameter || typeof parameter !== 'object' || Array.isArray(parameter)) {
      return
    }

    const record = parameter as { in?: unknown; name?: unknown }
    const key =
      typeof record.name === 'string'
        ? `${typeof record.in === 'string' ? record.in : 'unknown'}:${record.name}`
        : JSON.stringify(parameter)

    merged.set(key, parameter)
  })

  return Array.from(merged.values())
}

interface OpenAPIData {
  info: {
    title: string
    description: string
    server: string
  }
  routes: {
    path: string
    method: string
    operationId: string
    requestInBody?: boolean
  }[]
  functions: any
}

export const validateOpenAPI = async (openapiSpec: any) => {
  if (!openapiSpec.info) {
    throw new Error("('info'): field required")
  }

  if (!openapiSpec.info.title) {
    throw new Error("('info', 'title'): field required")
  }

  if (!openapiSpec.info.version) {
    throw new Error("('info', 'version'): field required")
  }

  if (!openapiSpec.servers || !openapiSpec.servers.length || !openapiSpec.servers[0].url) {
    throw new Error('Could not find a valid URL in `servers`')
  }

  if (!openapiSpec.paths || Object.keys(openapiSpec.paths).length === 0) {
    throw new Error('No paths found in the OpenAPI spec')
  }

  Object.keys(openapiSpec.paths).forEach((path) => {
    if (!path.startsWith('/')) {
      throw new Error(`Path ${path} does not start with a slash; skipping`)
    }
  })

  if (
    Object.values(openapiSpec.paths).some((methods: any) =>
      getOpenApiOperationEntries(methods).some(([, spec]: [string, any]) => !spec.operationId)
    )
  ) {
    throw new Error('Some methods are missing operationId')
  }

  if (
    Object.values(openapiSpec.paths).some((methods: any) =>
      getOpenApiOperationEntries(methods).some(
        ([, spec]: [string, any]) => spec.requestBody && !spec.requestBody.content
      )
    )
  ) {
    throw new Error('Some methods with a requestBody are missing requestBody.content')
  }

  if (
    Object.values(openapiSpec.paths).some((methods: any) =>
      getOpenApiOperationEntries(methods).some(([, spec]: [string, any]) => {
        if (spec.requestBody?.content?.['application/json']?.schema) {
          if (
            !spec.requestBody.content['application/json'].schema.properties ||
            Object.keys(spec.requestBody.content['application/json'].schema).length === 0
          ) {
            throw new Error(
              `In context=('paths', '${Object.keys(methods)[0]}', '${
                Object.keys(spec)[0]
              }', 'requestBody', 'content', 'application/json', 'schema'), object schema missing properties`
            )
          }
        }
      })
    )
  ) {
    throw new Error('Some object schemas are missing properties')
  }
}

export const openapiToFunctions = async (openapiSpec: any): Promise<OpenAPIData> => {
  const functions: any[] = [] // Define a proper type for function objects
  const routes: {
    path: string
    method: string
    operationId: string
    requestInBody?: boolean // Add a flag to indicate if the request should be in the body
  }[] = []

  for (const [path, methods] of Object.entries(openapiSpec.paths)) {
    if (typeof methods !== 'object' || methods === null) {
      continue
    }

    const pathParameters = getOpenApiParameters((methods as { parameters?: unknown }).parameters)

    for (const [method, specWithRef] of Object.entries(methods as Record<string, any>)) {
      if (!isOpenApiHttpMethod(method)) {
        continue
      }

      const spec: any = specWithRef
      if (!spec || typeof spec !== 'object' || typeof spec.operationId !== 'string') {
        continue
      }

      const functionName = spec.operationId
      const desc = spec.description || spec.summary || ''

      const schema: { type: string; properties: any; required?: string[] } = {
        type: 'object',
        properties: {}
      }

      const reqBody = spec.requestBody?.content?.['application/json']?.schema
      if (reqBody) {
        schema.properties.requestBody = reqBody
      }

      const params = mergeOpenApiParameters(
        pathParameters,
        getOpenApiParameters(spec.parameters)
      )
      if (params.length > 0) {
        const paramProperties = params.reduce((acc: any, param: any) => {
          if (param.schema) {
            acc[param.name] = param.schema
          }
          return acc
        }, {})

        schema.properties.parameters = {
          type: 'object',
          properties: paramProperties,
          required: params
            .filter((param: { name?: unknown; required?: unknown }) => {
              return param.required === true && typeof param.name === 'string'
            })
            .map((param: { name?: unknown }) => String(param.name))
        }
      }

      functions.push({
        type: 'function',
        function: {
          name: functionName,
          description: desc,
          parameters: schema
        }
      })

      // Determine if the request should be in the body based on the presence of requestBody
      const requestInBody = !!spec.requestBody

      routes.push({
        path,
        method,
        operationId: functionName,
        requestInBody // Include this flag in the route information
      })
    }
  }

  return {
    info: {
      title: openapiSpec.info.title,
      description: openapiSpec.info.description,
      server: openapiSpec.servers[0].url
    },
    routes,
    functions
  }
}
